import { useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays, MapPin } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { getPublicEvents } from "@/lib/api";

export default function EventsSection() {
  const [events, setEvents] = useState(null);

  useEffect(() => {
    getPublicEvents()
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]));
  }, []);

  return (
    <section className="bg-paper py-20 lg:py-28" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow className="mb-6">Gatherings</Eyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight mb-6">Upcoming events</h2>
          <p className="text-txtmut max-w-2xl leading-relaxed">
            Forest bathing walks, Gratitude Gatherings, readings, and workshops — announced here as they are scheduled.
          </p>
        </Reveal>

        <div className="mt-12">
          {events === null ? (
            <div className="border border-line rounded-lg bg-cream p-10 h-28 animate-pulse" data-testid="events-loading" />
          ) : events.length === 0 ? (
            <Reveal>
              <div className="border border-line rounded-lg bg-cream p-10 text-center" data-testid="events-empty">
                <CalendarDays className="w-8 h-8 text-terra mx-auto mb-4" strokeWidth={1.5} />
                <p className="font-serif text-2xl text-ink mb-2">New gatherings are announced seasonally</p>
                <p className="text-txtmut text-sm max-w-md mx-auto">
                  Check back soon — or subscribe to The Green Literacy Letter below to be the first to hear about walks and events.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="border-t border-line">
              {events.map((ev, i) => (
                <Reveal key={ev.id || i} delay={i * 0.06}>
                  <div className="group grid grid-cols-12 gap-4 sm:gap-8 py-8 border-b border-line items-center" data-testid={`event-card-${i + 1}`}>
                    <div className="col-span-12 sm:col-span-1">
                      <CalendarDays className="w-6 h-6 text-terra" strokeWidth={1.5} />
                    </div>
                    <div className="col-span-12 sm:col-span-6">
                      <h3 className="font-serif text-2xl text-ink leading-snug">{ev.title}</h3>
                      {ev.description && <p className="text-txtmut text-sm mt-2 leading-relaxed">{ev.description}</p>}
                    </div>
                    <div className="col-span-6 sm:col-span-3 font-mono text-[11px] uppercase tracking-[0.14em] text-txtmut space-y-2">
                      {ev.date_label && <p className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-terra" /> {ev.date_label}</p>}
                      {ev.location && <p className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-terra" /> {ev.location}</p>}
                    </div>
                    <div className="col-span-6 sm:col-span-2 text-right">
                      {ev.url ? (
                        <a
                          href={ev.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300"
                          data-testid={`event-link-${i + 1}`}
                        >
                          Sign up <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                      ) : (
                        <a href="#booking" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-terra link-underline">
                          Reserve a spot
                        </a>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
